import { log } from './logging';
import type { PerformancePointLegacy } from './performanceLegacy';

export enum PerformancePoint {
  PageLoaded = 'PageLoaded', // Triggered as soon as first script execution in webpage is run
  AppInitiated = 'AppInitiated', // Triggered when root Vue component App.vue is mounted

  // Itinerary prices
  ItineraryPricesRequested = 'ItineraryPricesRequested', // Triggered on SmartSearch request
  ItineraryPricesReceived = 'ItineraryPricesReceived', // Triggered on SmartSearch response
  ItineraryPricesRendered = 'ItineraryPricesRendered', // Triggered on first day rendered in PriceRibbon

  // BuildYourTrip page
  BuildYourTripPageNavigationInitiated = 'BuildYourTripPageNavigationInitiated', // Triggered on navigation initiated
  BuildYourTripPageRendered = 'BuildYourTripPageRendered', // Triggered on first render, potentially with loading state

  // TripOverview page
  TripOverviewPageNavigationInitiated = 'TripOverviewPageNavigationInitiated', // Triggered on navigation initiated
  TripOverviewPageRendered = 'TripOverviewPageRendered', // Triggered on first render, potentially with loading state
  TripOverviewPageRenderedWithContent = 'TripOverviewPageRenderedWithContent', // Triggered on render with most relevant content

  // HotelSelection page
  HotelSelectionPageNavigationInitiated = 'HotelSelectionPageNavigationInitiated', // Triggered on navigation initiated
  HotelSelectionPageRendered = 'HotelSelectionPageRendered', // Triggered on first render, potentially with loading state
  HotelSelectionPageRenderedWithContent = 'HotelSelectionPageRenderedWithContent', // Triggered on render with most relevant content

  // HotelDetails page
  HotelDetailsPageNavigationInitiated = 'HotelDetailsPageNavigationInitiated', // Triggered on navigation initiated
  HotelDetailsPageRendered = 'HotelDetailsPageRendered', // Triggered on first render, potentially with loading state
  HotelDetailsPageRenderedWithContent = 'HotelDetailsPageRenderedWithContent', // Triggered on render with most relevant content

  // ActivitySelection page
  ActivitySelectionPageNavigationInitiated = 'ActivitySelectionPageNavigationInitiated', // Triggered on navigation initiated
  ActivitySelectionPageRendered = 'ActivitySelectionPageRendered', // Triggered on first render, potentially with loading state
  ActivitySelectionPageRenderedWithContent = 'ActivitySelectionPageRenderedWithContent', // Triggered on render with most relevant content

  // ActivityDetails page
  ActivityDetailsPageNavigationInitiated = 'ActivityDetailsPageNavigationInitiated', // Triggered on navigation initiated
  ActivityDetailsPageRendered = 'ActivityDetailsPageRendered', // Triggered on first render, potentially with loading state
  ActivityDetailsPageRenderedWithContent = 'ActivityDetailsPageRenderedWithContent', // Triggered on render with most relevant content

  // FlightSelection page
  FlightSelectionPageNavigationInitiated = 'FlightSelectionPageNavigationInitiated', // Triggered on navigation initiated
  FlightSelectionPageRendered = 'FlightSelectionPageRendered', // Triggered on first render, potentially with loading state
  FlightSelectionPageRenderedWithContent = 'FlightSelectionPageRenderedWithContent', // Triggered on render with most relevant content

  // FerrySelection page
  FerrySelectionPageNavigationInitiated = 'FerrySelectionPageNavigationInitiated', // Triggered on navigation initiated
  FerrySelectionPageRendered = 'FerrySelectionPageRendered', // Triggered on first render, potentially with loading state
  FerrySelectionPageRenderedWithContent = 'FerrySelectionPageRenderedWithContent', // Triggered on render with most relevant content
}

export function performanceMark(
  point: PerformancePoint | PerformancePointLegacy
): PerformanceMark {
  log({ title: 'Performance', context: [point, performance.now()] });
  return performance.mark(point);
}

export function performanceClearMarks(
  point?: PerformancePoint | PerformancePointLegacy
): void {
  performance.clearMarks(point);
}

export function performanceMeasure(
  firstPoint: PerformancePoint | PerformancePointLegacy,
  secondPoint?: PerformancePoint | PerformancePointLegacy
): PerformanceMeasure {
  if (!secondPoint) {
    return performance.measure(`initial-${firstPoint}`, {
      duration: performance.getEntriesByName(firstPoint)[0]?.startTime,
      end: firstPoint,
    });
  }

  return performance.measure(`${firstPoint}-${secondPoint}`, {
    start: firstPoint,
    end: secondPoint,
  });
}

function measurePerformance<T>(
  performancePoints: PerformancePoint[],
  callback: () => T
): Promise<T> {
  return new Promise((resolve) => {
    const observerCallback: PerformanceObserverCallback = (_, observer) => {
      const hasAllPerformancePoints = performancePoints.every(
        (performancePoint) =>
          performance.getEntriesByName(performancePoint).length > 0
      );

      if (hasAllPerformancePoints) {
        observer.disconnect();

        resolve(callback());
      }
    };

    const observer = new PerformanceObserver(observerCallback);

    observer.observe({ entryTypes: ['mark'] });
  });
}

interface InitializationPerformanceMeasures {
  timeToPageLoadedMs: number;
  timeToAppInitiatedMs: number;
}
export function measureInitializationPerformance(): Promise<InitializationPerformanceMeasures> {
  return measurePerformance(
    [PerformancePoint.AppInitiated, PerformancePoint.PageLoaded],
    () => ({
      timeToPageLoadedMs: performanceMeasure(PerformancePoint.PageLoaded)
        .duration,
      timeToAppInitiatedMs: performanceMeasure(
        PerformancePoint.PageLoaded,
        PerformancePoint.AppInitiated
      ).duration,
    })
  );
}

interface ItineraryPricesPerformanceMeasures {
  timeToRequestMs: number;
  timeToReceiveMs: number;
  timeToRenderMs: number;
}
export function measureItineraryPricesPerformance(): Promise<ItineraryPricesPerformanceMeasures> {
  return measurePerformance(
    [
      PerformancePoint.AppInitiated,
      PerformancePoint.ItineraryPricesRequested,
      PerformancePoint.ItineraryPricesReceived,
      PerformancePoint.ItineraryPricesRendered,
    ],
    () => ({
      timeToRequestMs: performanceMeasure(
        PerformancePoint.AppInitiated,
        PerformancePoint.ItineraryPricesRequested
      ).duration,
      timeToReceiveMs: performanceMeasure(
        PerformancePoint.ItineraryPricesRequested,
        PerformancePoint.ItineraryPricesReceived
      ).duration,
      timeToRenderMs: performanceMeasure(
        PerformancePoint.ItineraryPricesReceived,
        PerformancePoint.ItineraryPricesRendered
      ).duration,
    })
  );
}

interface PagePerformanceMeasures {
  timeToRenderMs: number;
  timeToRenderWithContentMs: number;
}

export function measureBuildYourTripPagePerformance(): Promise<
  Pick<PagePerformanceMeasures, 'timeToRenderMs'>
> {
  return measurePerformance(
    [
      PerformancePoint.BuildYourTripPageNavigationInitiated,
      PerformancePoint.BuildYourTripPageRendered,
    ],
    () => ({
      timeToRenderMs: performanceMeasure(
        PerformancePoint.BuildYourTripPageNavigationInitiated,
        PerformancePoint.BuildYourTripPageRendered
      ).duration,
    })
  );
}

export function measureTripOverviewPagePerformance(): Promise<PagePerformanceMeasures> {
  return measurePerformance(
    [
      PerformancePoint.TripOverviewPageNavigationInitiated,
      PerformancePoint.TripOverviewPageRendered,
      PerformancePoint.TripOverviewPageRenderedWithContent,
    ],
    () => ({
      timeToRenderMs: performanceMeasure(
        PerformancePoint.TripOverviewPageNavigationInitiated,
        PerformancePoint.TripOverviewPageRendered
      ).duration,
      timeToRenderWithContentMs: performanceMeasure(
        PerformancePoint.TripOverviewPageNavigationInitiated,
        PerformancePoint.TripOverviewPageRenderedWithContent
      ).duration,
    })
  );
}

export function measureHotelSelectionPagePerformance(): Promise<PagePerformanceMeasures> {
  return measurePerformance(
    [
      PerformancePoint.HotelSelectionPageNavigationInitiated,
      PerformancePoint.HotelSelectionPageRendered,
      PerformancePoint.HotelSelectionPageRenderedWithContent,
    ],
    () => ({
      timeToRenderMs: performanceMeasure(
        PerformancePoint.HotelSelectionPageNavigationInitiated,
        PerformancePoint.HotelSelectionPageRendered
      ).duration,
      timeToRenderWithContentMs: performanceMeasure(
        PerformancePoint.HotelSelectionPageNavigationInitiated,
        PerformancePoint.HotelSelectionPageRenderedWithContent
      ).duration,
    })
  );
}

export function measureHotelDetailsPagePerformance(): Promise<PagePerformanceMeasures> {
  return measurePerformance(
    [
      PerformancePoint.HotelDetailsPageNavigationInitiated,
      PerformancePoint.HotelDetailsPageRendered,
      PerformancePoint.HotelDetailsPageRenderedWithContent,
    ],
    () => ({
      timeToRenderMs: performanceMeasure(
        PerformancePoint.HotelDetailsPageNavigationInitiated,
        PerformancePoint.HotelDetailsPageRendered
      ).duration,
      timeToRenderWithContentMs: performanceMeasure(
        PerformancePoint.HotelDetailsPageNavigationInitiated,
        PerformancePoint.HotelDetailsPageRenderedWithContent
      ).duration,
    })
  );
}

export function measureActivitySelectionPagePerformance(): Promise<PagePerformanceMeasures> {
  return measurePerformance(
    [
      PerformancePoint.ActivitySelectionPageNavigationInitiated,
      PerformancePoint.ActivitySelectionPageRendered,
      PerformancePoint.ActivitySelectionPageRenderedWithContent,
    ],
    () => ({
      timeToRenderMs: performanceMeasure(
        PerformancePoint.ActivitySelectionPageNavigationInitiated,
        PerformancePoint.ActivitySelectionPageRendered
      ).duration,
      timeToRenderWithContentMs: performanceMeasure(
        PerformancePoint.ActivitySelectionPageNavigationInitiated,
        PerformancePoint.ActivitySelectionPageRenderedWithContent
      ).duration,
    })
  );
}

export function measureActivityDetailsPagePerformance(): Promise<PagePerformanceMeasures> {
  return measurePerformance(
    [
      PerformancePoint.ActivityDetailsPageNavigationInitiated,
      PerformancePoint.ActivityDetailsPageRendered,
      PerformancePoint.ActivityDetailsPageRenderedWithContent,
    ],
    () => ({
      timeToRenderMs: performanceMeasure(
        PerformancePoint.ActivityDetailsPageNavigationInitiated,
        PerformancePoint.ActivityDetailsPageRendered
      ).duration,
      timeToRenderWithContentMs: performanceMeasure(
        PerformancePoint.ActivityDetailsPageNavigationInitiated,
        PerformancePoint.ActivityDetailsPageRenderedWithContent
      ).duration,
    })
  );
}

export function measureFlightSelectionPagePerformance(): Promise<PagePerformanceMeasures> {
  return measurePerformance(
    [
      PerformancePoint.FlightSelectionPageNavigationInitiated,
      PerformancePoint.FlightSelectionPageRendered,
      PerformancePoint.FlightSelectionPageRenderedWithContent,
    ],
    () => ({
      timeToRenderMs: performanceMeasure(
        PerformancePoint.FlightSelectionPageNavigationInitiated,
        PerformancePoint.FlightSelectionPageRendered
      ).duration,
      timeToRenderWithContentMs: performanceMeasure(
        PerformancePoint.FlightSelectionPageNavigationInitiated,
        PerformancePoint.FlightSelectionPageRenderedWithContent
      ).duration,
    })
  );
}

export function measureFerrySelectionPagePerformance(): Promise<PagePerformanceMeasures> {
  return measurePerformance(
    [
      PerformancePoint.FerrySelectionPageNavigationInitiated,
      PerformancePoint.FerrySelectionPageRendered,
      PerformancePoint.FerrySelectionPageRenderedWithContent,
    ],
    () => ({
      timeToRenderMs: performanceMeasure(
        PerformancePoint.FerrySelectionPageNavigationInitiated,
        PerformancePoint.FerrySelectionPageRendered
      ).duration,
      timeToRenderWithContentMs: performanceMeasure(
        PerformancePoint.FerrySelectionPageNavigationInitiated,
        PerformancePoint.FerrySelectionPageRenderedWithContent
      ).duration,
    })
  );
}
